import { useState } from 'react';
import { ErrorMsg, Modal } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { StudioDetail } from '../../lib/types';
import { channelLabel } from './OverviewTab';

const CHANNEL_HELP: Record<string, string> = {
  website: 'collected: pages, changes',
  blog: 'collected from the RSS/Atom feed the homepage advertises',
  vimeo: 'collected from the public Vimeo feed (needs the Vimeo link)',
  youtube: 'checked weekly; YouTube’s robots.txt currently blocks its public feed',
};

export function PromoteDialog({ studio, onClose, onDone }: { studio: StudioDetail; onClose: () => void; onDone: (msg: string) => void }) {
  const { settings } = useSession();
  const known = settings?.known_channels ?? ['website'];
  const supported = new Set(settings?.supported_channels ?? ['website']);
  const notPermitted = settings?.not_permitted_channels ?? {};
  const defaults = settings?.values.deep_defaults ?? { cadence_days: 7, lookback_days: 90, channels: ['website'] };
  const current = studio.research_depth === 'deep' ? studio.deep_settings : null;
  const [channels, setChannels] = useState<string[]>(current?.channels ?? defaults.channels);
  const [cadence, setCadence] = useState(current?.cadence_days ?? defaults.cadence_days);
  const [lookback, setLookback] = useState(current?.lookback_days ?? defaults.lookback_days);
  const [maxPages, setMaxPages] = useState(current?.max_pages ?? settings?.values.collection.max_pages_deep ?? 12);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isDeep = studio.research_depth === 'deep';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api<{ research_depth: string; channel_status: Record<string, string> }>(`/api/studios/${studio.id}/research-mode`, {
        method: 'PATCH',
        body: { mode: 'deep', deep: { channels, cadence_days: cadence, lookback_days: lookback, max_pages: maxPages } },
      });
      const scheduled = Object.entries(r.channel_status)
        .filter(([, s]) => s === 'scheduled')
        .map(([c]) => c);
      const none = Object.entries(r.channel_status)
        .filter(([, s]) => s !== 'scheduled')
        .map(([c, s]) => `${c} (${channelLabel(s)})`);
      onDone(
        `${isDeep ? 'Deep settings saved' : 'Promoted to Deep'}. Collecting: ${scheduled.join(', ') || 'nothing'}` +
          (none.length ? `. Watched but not collected: ${none.join(', ')}.` : '.'),
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isDeep ? 'Deep research settings' : `Promote ${studio.name} to Deep`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="promote-form" className="btn primary" disabled={busy || channels.length === 0}>
            {busy ? 'Saving…' : isDeep ? 'Save settings' : 'Promote to Deep'}
          </button>
        </>
      }
    >
      <form id="promote-form" onSubmit={submit} className="stack" aria-label="Deep research settings">
        <p className="small">
          Deep mode collects this studio’s sources on a schedule and keeps every snapshot as evidence. Only channels with an adapter are actually
          collected.
        </p>
        <fieldset className="field">
          <legend>Channels</legend>
          {known.map((c) => {
            const ok = supported.has(c);
            return (
              <label key={c} className="check block">
                <input
                  type="checkbox"
                  checked={channels.includes(c)}
                  onChange={(e) => setChannels(e.target.checked ? [...channels, c] : channels.filter((x) => x !== c))}
                />{' '}
                {c}
                {notPermitted[c] ? (
                  <span className="muted small"> — not permitted ({notPermitted[c]}), not collected</span>
                ) : ok ? (
                  <span className="muted small"> — {CHANNEL_HELP[c] ?? 'collected'}</span>
                ) : (
                  <span className="muted small"> — no adapter yet, not collected</span>
                )}
              </label>
            );
          })}
        </fieldset>
        <div className="row gap wrap">
          <label className="field">
            <span>Cadence (days, 1–90)</span>
            <input type="number" min={1} max={90} value={cadence} onChange={(e) => setCadence(Number(e.target.value))} />
          </label>
          <label className="field">
            <span>Lookback (days, 7–365)</span>
            <input type="number" min={7} max={365} value={lookback} onChange={(e) => setLookback(Number(e.target.value))} />
          </label>
          <label className="field">
            <span>Max pages per run (1–50)</span>
            <input type="number" min={1} max={50} value={maxPages} onChange={(e) => setMaxPages(Number(e.target.value))} />
          </label>
        </div>
        {studio.is_demo && (
          <p className="msg warn small">This is a fictional demo studio on a reserved .example domain: it becomes Deep, but no website collection is scheduled for it.</p>
        )}
      </form>
      <ErrorMsg>{error}</ErrorMsg>
    </Modal>
  );
}

export function DemoteDialog({ studio, onClose, onDone }: { studio: StudioDetail; onClose: () => void; onDone: (msg: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function demote() {
    setBusy(true);
    setError('');
    try {
      const r = await api<{ schedules_ended: number; jobs_cancelled: number }>(`/api/studios/${studio.id}/research-mode`, {
        method: 'PATCH',
        body: { mode: 'light' },
      });
      onDone(`Returned to Light. ${r.schedules_ended} schedule(s) ended, ${r.jobs_cancelled} queued job(s) cancelled. Collected history is kept.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Return ${studio.name} to Light`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn danger" onClick={() => void demote()} disabled={busy}>
            {busy ? 'Demoting…' : 'Demote to Light'}
          </button>
        </>
      }
    >
      <p>Scheduled collection for this studio stops: its schedules end and queued Deep jobs are cancelled. A running job is asked to stop.</p>
      <p>
        <strong>Nothing collected so far is deleted.</strong> Sources, snapshots, facts and notes stay, and you can promote the studio again later.
      </p>
      <ErrorMsg>{error}</ErrorMsg>
    </Modal>
  );
}
